/**
 * Reports View - Embedded report builder for Analyst Bench
 *
 * Two types of reports:
 * 1. Rating Reports - Analyst-written narrative with approval workflow
 * 2. Data Reports - Auto-generated COAC reports with charts/tables
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalystBench } from '../../../context/AnalystBenchContext';
import { useReportGeneration } from '../../../context/ReportGenerationContext';
import { useAuth } from '../../../context/AuthContext';
import { useRoleAssumption } from '../../../context/RoleAssumptionContext';
import type { ReportGenerationOptions, ReportGenerationProgress } from '../../../types/report';
import {
  checkCachedReport,
  ReportMetadata,
  getRelativeTime,
  startDraftGeneration,
  pollDraftCompletion,
  type DraftGenerationJob,
} from '../../../services/reportService';
import {
  getReportForInstitutionPeriod,
  getUserProfile,
  type ReportWorkflow,
} from '../../../services/firestore';
import StatusBadge from '../../../components/reports/StatusBadge';
import './ReportsView.css';

// Timeline event type
interface TimelineEvent {
  id: string;
  label: string;
  userId: string | null;
  userName: string | null;
  date: Date | null;
  status: 'completed' | 'current' | 'pending';
  notes?: string | null;
}

// Build timeline events from report workflow
function buildTimelineEvents(report: ReportWorkflow, t: (key: string) => string): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // 1. Report Started (draft created by analyst)
  events.push({
    id: 'started',
    label: t('timeline.reportStarted'),
    userId: report.created_by,
    userName: null,
    date: report.created_at?.toDate() || null,
    status: 'completed',
    notes: null,
  });

  // 2. AI Draft Generated (by BankingLATAM AI)
  if (report.ai_generated_at) {
    events.push({
      id: 'ai_generated',
      label: t('timeline.aiDraftGenerated'),
      userId: null,
      userName: 'BankingLATAM AI',
      date: report.ai_generated_at?.toDate() || null,
      status: 'completed',
      notes: report.ai_generation_model ? `${t('timeline.model')}${report.ai_generation_model}` : null,
    });
  }

  // 3. Analyst Edited (first save after AI generation)
  if (report.first_edited_at) {
    events.push({
      id: 'analyst_edited',
      label: t('timeline.analystEdited'),
      userId: report.first_edited_by,
      userName: null,
      date: report.first_edited_at?.toDate() || null,
      status: 'completed',
      notes: null,
    });
  } else if (report.ai_generated_at && report.status === 'draft') {
    // Show as pending if AI generated but not yet edited
    events.push({
      id: 'analyst_edited',
      label: t('timeline.analystEdit'),
      userId: null,
      userName: null,
      date: null,
      status: 'current',
      notes: null,
    });
  }

  // 4. Submitted for admin review
  if (report.submitted_at) {
    events.push({
      id: 'submitted',
      label: t('timeline.submittedForReview'),
      userId: report.analyst_id,
      userName: null,
      date: report.submitted_at?.toDate() || null,
      status: 'completed',
      notes: null,
    });
  } else if (report.status === 'draft') {
    events.push({
      id: 'submitted',
      label: t('timeline.submitForReview'),
      userId: null,
      userName: null,
      date: null,
      status: 'pending',
      notes: null,
    });
  }

  // 3. Admin review
  if (report.admin_reviewed_at) {
    events.push({
      id: 'admin_review',
      label: t('timeline.adminReview'),
      userId: report.admin_reviewed_by,
      userName: null,
      date: report.admin_reviewed_at.toDate(),
      status: 'completed',
      notes: report.admin_comments || null,
    });
  } else if (report.status === 'pending_admin') {
    events.push({
      id: 'admin_review',
      label: t('timeline.adminReview'),
      userId: null,
      userName: null,
      date: null,
      status: 'current',
      notes: null,
    });
  } else if (['client_review', 'pending_committee', 'approved'].includes(report.status)) {
    events.push({
      id: 'admin_review',
      label: t('timeline.adminReview'),
      userId: report.admin_reviewed_by,
      userName: null,
      date: null,
      status: 'completed',
      notes: report.admin_comments || null,
    });
  } else if (report.status !== 'draft' && report.status !== 'rejected') {
    events.push({
      id: 'admin_review',
      label: t('timeline.adminReview'),
      userId: null,
      userName: null,
      date: null,
      status: 'pending',
      notes: null,
    });
  }

  // 4. Client review (if applicable)
  if (report.client_reviewed_at) {
    events.push({
      id: 'client_review',
      label: t('timeline.clientReview'),
      userId: report.client_reviewed_by,
      userName: null,
      date: report.client_reviewed_at.toDate(),
      status: 'completed',
      notes: report.client_additional_info || (report.client_comments.length > 0 ? `${report.client_comments.length} ${t('timeline.clientComments')}` : null),
    });
  } else if (report.status === 'client_review') {
    events.push({
      id: 'client_review',
      label: t('timeline.clientReview'),
      userId: null,
      userName: null,
      date: null,
      status: 'current',
      notes: null,
    });
  } else if (['pending_committee', 'approved'].includes(report.status)) {
    if (report.client_review_started_at) {
      events.push({
        id: 'client_review',
        label: t('timeline.clientReview'),
        userId: report.client_reviewed_by,
        userName: null,
        date: null,
        status: 'completed',
        notes: report.client_additional_info || (report.client_comments.length > 0 ? `${report.client_comments.length} ${t('timeline.clientComments')}` : null),
      });
    }
  }

  // 5. Committee approval
  if (report.committee_reviewed_at) {
    events.push({
      id: 'committee',
      label: t('timeline.committeeApproval'),
      userId: report.committee_reviewed_by,
      userName: null,
      date: report.committee_reviewed_at.toDate(),
      status: 'completed',
      notes: report.committee_comments || null,
    });
  } else if (report.status === 'pending_committee') {
    events.push({
      id: 'committee',
      label: t('timeline.committeeApproval'),
      userId: null,
      userName: null,
      date: null,
      status: 'current',
      notes: null,
    });
  } else if (report.status === 'approved') {
    events.push({
      id: 'committee',
      label: t('timeline.committeeApproval'),
      userId: report.committee_reviewed_by,
      userName: null,
      date: null,
      status: 'completed',
      notes: report.committee_comments || null,
    });
  } else if (!['draft', 'pending_admin', 'rejected'].includes(report.status)) {
    events.push({
      id: 'committee',
      label: t('timeline.committeeApproval'),
      userId: null,
      userName: null,
      date: null,
      status: 'pending',
      notes: null,
    });
  }

  // 6. Published (final step)
  if (report.published_at) {
    events.push({
      id: 'published',
      label: t('timeline.published'),
      userId: null,
      userName: null,
      date: report.published_at?.toDate() || null,
      status: 'completed',
      notes: null,
    });
  } else if (report.status === 'approved' && !report.published_at) {
    events.push({
      id: 'published',
      label: t('timeline.publish'),
      userId: null,
      userName: null,
      date: null,
      status: 'current',
      notes: null,
    });
  }

  // Handle rejection
  if (report.rejected_at) {
    events.push({
      id: 'rejected',
      label: t('timeline.rejected'),
      userId: report.rejected_by,
      userName: null,
      date: report.rejected_at?.toDate() || null,
      status: 'completed',
      notes: report.rejection_reason || null,
    });
  }

  return events;
}

export default function ReportsView() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectedInstitution, availablePeriods, reportsPeriod, setReportsPeriod } = useAnalystBench();
  const { startGeneration, activeJobs } = useReportGeneration();
  const { userProfile } = useAuth();
  const { effectiveOrganization } = useRoleAssumption();

  const institutionId = selectedInstitution?.id || '';
  const institutionName = selectedInstitution?.name || '';

  // Selected period for report generation - use context period if set, otherwise default to latest
  const [selectedPeriod, setSelectedPeriodLocal] = useState<string>(
    reportsPeriod || availablePeriods[0] || ''
  );

  // Sync from context when reportsPeriod changes (e.g., from URL param navigation)
  useEffect(() => {
    if (reportsPeriod && availablePeriods.includes(reportsPeriod)) {
      setSelectedPeriodLocal(reportsPeriod);
      // Clear the context period after applying it
      setReportsPeriod(null);
    }
  }, [reportsPeriod, availablePeriods, setReportsPeriod]);

  // Update selected period when available periods change (and no context period)
  useEffect(() => {
    if (availablePeriods.length > 0 && !selectedPeriod && !reportsPeriod) {
      setSelectedPeriodLocal(availablePeriods[0]);
    }
  }, [availablePeriods, selectedPeriod, reportsPeriod]);

  // Wrapper to update local state
  const setSelectedPeriod = (period: string) => {
    setSelectedPeriodLocal(period);
  };

  // Rating Report (workflow) state
  const [ratingReport, setRatingReport] = useState<ReportWorkflow | null>(null);
  const [loadingRatingReport, setLoadingRatingReport] = useState(false);

  // Draft generation progress state
  const [draftGenerationProgress, setDraftGenerationProgress] = useState<{
    isGenerating: boolean;
    progress: number;
    currentSection: string;
    message: string;
  }>({
    isGenerating: false,
    progress: 0,
    currentSection: '',
    message: '',
  });

  // Timeline state
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [userNamesCache, setUserNamesCache] = useState<Record<string, string>>({});
  const [expandedTimelineItems, setExpandedTimelineItems] = useState<Set<string>>(new Set());

  // Cached report state (for data reports)
  const [cachedReport, setCachedReport] = useState<ReportMetadata | null>(null);
  const [checkingCache, setCheckingCache] = useState(false);

  // Report options state
  const [options, setOptions] = useState<ReportGenerationOptions>({
    institution_id: institutionId,
    period: selectedPeriod,
    sections: {
      riesgo_entidad: false,
      estructura_balance: true,
      indicadores_financieros: true,
      anexo_1: true,
    },
    custom_notes: '',
    include_charts: true,
    include_tables: true,
  });

  // Hierarchical selection state
  const [estructuraBalance, setEstructuraBalance] = useState({
    activo: { selected: true },
    pasivo: { selected: true },
    patrimonio: { selected: true },
  });

  const [indicadoresFinancieros, setIndicadoresFinancieros] = useState({
    solvencia: { selected: true },
    liquidez: { selected: true },
    calidad_activos: { selected: true },
    eficiencia_gestion: { selected: true },
    rentabilidad: { selected: true },
    riesgo_interes: { selected: true },
  });

  // Standalone sections
  const [includeAnexo1, setIncludeAnexo1] = useState(true);

  // Drawer/collapse states
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    sections: true,
    estructura_balance: false,
    indicadores_financieros: false,
  });

  // Generation progress
  const [progress, setProgress] = useState<ReportGenerationProgress>({
    status: 'idle',
    progress: 0,
    message: 'Ready to generate report',
  });


  // Check for rating report (workflow) when period changes
  useEffect(() => {
    if (institutionId && selectedPeriod && effectiveOrganization?.id) {
      loadRatingReport();
    }
  }, [institutionId, selectedPeriod, effectiveOrganization?.id]);

  const loadRatingReport = async () => {
    if (!effectiveOrganization?.id) return;
    try {
      setLoadingRatingReport(true);
      const report = await getReportForInstitutionPeriod(
        institutionId,
        selectedPeriod,
        effectiveOrganization.id
      );
      setRatingReport(report);
    } catch (error) {
      console.error('Error loading rating report:', error);
      setRatingReport(null);
    } finally {
      setLoadingRatingReport(false);
    }
  };

  // Build timeline and fetch user names when rating report changes
  useEffect(() => {
    if (!ratingReport) {
      setTimelineEvents([]);
      return;
    }

    // Build initial timeline events
    const events = buildTimelineEvents(ratingReport, t);
    setTimelineEvents(events);

    // Collect unique user IDs that need names
    const userIds = events
      .filter(e => e.userId && !userNamesCache[e.userId])
      .map(e => e.userId!)
      .filter((id, index, arr) => arr.indexOf(id) === index);

    // Fetch user names
    if (userIds.length > 0) {
      Promise.all(
        userIds.map(async (userId) => {
          try {
            const profile = await getUserProfile(userId);
            return { userId, name: profile?.display_name || profile?.email || 'Unknown' };
          } catch {
            return { userId, name: 'Unknown' };
          }
        })
      ).then((results) => {
        const newCache = { ...userNamesCache };
        results.forEach(({ userId, name }) => {
          newCache[userId] = name;
        });
        setUserNamesCache(newCache);
      });
    }
  }, [ratingReport]);

  // Start a new rating report with AI draft generation
  const handleStartRatingReport = async () => {
    if (!userProfile?.uid || !effectiveOrganization?.id) return;

    try {
      // Start generating state
      setDraftGenerationProgress({
        isGenerating: true,
        progress: 0,
        currentSection: '',
        message: 'Starting AI draft generation...',
      });

      // Set due date to 30 days from now
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30);

      // Start the background draft generation
      const { job_id } = await startDraftGeneration({
        institution_id: institutionId,
        period: selectedPeriod,
        org_id: effectiveOrganization.id,
        analyst_id: userProfile.uid,
        final_due_date: dueDate.toISOString(),
      });

      // Poll for completion with progress updates
      const completedJob = await pollDraftCompletion(job_id, {
        intervalMs: 2000,
        maxAttempts: 120, // 4 minutes max
        onProgress: (job: DraftGenerationJob) => {
          setDraftGenerationProgress({
            isGenerating: true,
            progress: job.progress || 0,
            currentSection: job.current_section || '',
            message: job.message || `Generating sections... ${job.progress || 0}%`,
          });
        },
      });

      // Reset progress state
      setDraftGenerationProgress({
        isGenerating: false,
        progress: 100,
        currentSection: '',
        message: 'Draft generated successfully!',
      });

      // Reload the rating report to show it in the list (don't auto-navigate to editor)
      console.log('[handleStartRatingReport] Draft generated, reloading report list');
      await loadRatingReport();
    } catch (error) {
      console.error('Error creating rating report:', error);
      setDraftGenerationProgress({
        isGenerating: false,
        progress: 0,
        currentSection: '',
        message: '',
      });
      alert(error instanceof Error ? error.message : 'Failed to generate report draft');
    }
  };

  // Check for cached report when period changes
  useEffect(() => {
    if (institutionId && selectedPeriod) {
      checkCache();
    }
  }, [institutionId, selectedPeriod]);

  const checkCache = async () => {
    try {
      setCheckingCache(true);
      const result = await checkCachedReport(institutionId, selectedPeriod);
      if (result.cached && result.report) {
        setCachedReport(result.report);
      } else {
        setCachedReport(null);
      }
    } catch (error) {
      console.error('Error checking cache:', error);
      setCachedReport(null);
    } finally {
      setCheckingCache(false);
    }
  };

  // Check if there's an active job for this institution/period
  const activeJob = activeJobs.find(
    job => job.institutionId === institutionId && job.period === selectedPeriod
  );

  const handleSectionToggle = (section: keyof typeof options.sections) => {
    setOptions({
      ...options,
      sections: {
        ...options.sections,
        [section]: !options.sections[section],
      },
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Estructura de Balance handlers
  const handleEstructuraSubsectionToggle = (subsection: 'activo' | 'pasivo' | 'patrimonio') => {
    setEstructuraBalance((prev) => ({
      ...prev,
      [subsection]: {
        ...prev[subsection],
        selected: !prev[subsection].selected,
      },
    }));
  };

  // Indicadores Financieros handlers
  const handleIndicadoresSubsectionToggle = (
    subsection: 'solvencia' | 'liquidez' | 'calidad_activos' | 'eficiencia_gestion' | 'rentabilidad' | 'riesgo_interes'
  ) => {
    setIndicadoresFinancieros((prev) => ({
      ...prev,
      [subsection]: {
        ...prev[subsection],
        selected: !prev[subsection].selected,
      },
    }));
  };

  // View existing cached report within Analyst Bench
  const handleViewReport = (reportId: string) => {
    navigate(`/analyst-bench/${institutionId}/reports/${reportId}`);
  };

  // Start background generation
  const handleGenerateReport = async () => {
    try {
      setProgress({
        status: 'fetching_data',
        progress: 10,
        message: 'Starting background generation...',
      });

      await startGeneration({
        institution_id: institutionId,
        period: selectedPeriod,
        sections: options.sections,
        institutionName,
      });

      setProgress({
        status: 'completed',
        progress: 100,
        message: 'Generation started. You can continue working.',
      });

      // Refresh cache after a delay
      setTimeout(() => {
        checkCache();
      }, 2000);

      // Reset progress after showing success
      setTimeout(() => {
        setProgress({
          status: 'idle',
          progress: 0,
          message: 'Ready to generate report',
        });
      }, 3000);

    } catch (error) {
      console.error('Report generation error:', error);
      setProgress({
        status: 'error',
        progress: 0,
        message: 'Error starting generation',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const formatPeriodLabel = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  };

  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('time.justNow');
    if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
    return t('time.daysAgo', { count: diffDays });
  };

  const formatTimelineDate = (date: Date | null): string => {
    if (!date) return '';
    const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get user name from cache or show loading placeholder
  const getTimelineUserName = (userId: string | null): string => {
    if (!userId) return '';
    return userNamesCache[userId] || 'Loading...';
  };

  // Toggle timeline item expansion
  const toggleTimelineItem = (eventId: string) => {
    setExpandedTimelineItems(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  // Get step number based on event id
  const getStepNumber = (eventId: string): string => {
    const stepMap: Record<string, string> = {
      started: '1',
      ai_generated: '2',
      analyst_edited: '3',
      submitted: '4',
      admin_review: '5',
      client_review: '6',
      committee: '7',
      published: '8',
      rejected: 'X',
    };
    return stepMap[eventId] || '?';
  };

  if (!selectedInstitution) {
    return (
      <div className="reports-view">
        <div className="reports-empty-state">
          <p>{t('reportsView.selectInstitution')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-view">
      {/* Header */}
      <div className="reports-view-header">
        <div>
          <h2>{t('reportsView.title')}</h2>
          <p className="reports-view-subtitle">
            {t('reportsView.subtitle', { name: institutionName })}
          </p>
        </div>
        <div className="reports-header-actions">
          <div className="reports-period-selector">
            <label htmlFor="report-period">{t('reportsView.period')}</label>
            <select
              id="report-period"
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="period-select"
            >
              {availablePeriods.map(p => (
                <option key={p} value={p}>{formatPeriodLabel(p)}</option>
              ))}
            </select>
          </div>
          {/* Generate Report button - hidden for now, will be used later
          <button
            className="btn-primary btn-generate"
            onClick={handleGenerateReport}
            disabled={
              progress.status !== 'idle' && progress.status !== 'error' ||
              (!options.sections.estructura_balance && !options.sections.indicadores_financieros)
            }
          >
            {progress.status === 'idle' || progress.status === 'error' ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Generate Report
              </>
            ) : (
              'Generating...'
            )}
          </button>
          */}
        </div>
      </div>

      {/* Main Content - Single Column */}
      <div className="reports-builder-panel">
        {/* Rating Report Section */}
        <div className="rating-report-section">
          <div className="section-header-with-badge">
            <h3>{t('reportsView.ratingReport')}</h3>
            <span className="section-badge">{t('reportsView.workflow')}</span>
          </div>

          {loadingRatingReport ? (
            <div className="rating-report-loading">
              <div className="generating-spinner" />
              <span>{t('reportsView.checkingReport')}</span>
            </div>
          ) : ratingReport ? (
            <div className="rating-report-card">
              <div className="rating-report-card-header">
                <div className="rating-report-info">
                  <div className="rating-report-header">
                    <StatusBadge status={ratingReport.status} size="small" />
                    <span className="rating-report-version">v{ratingReport.version}</span>
                  </div>
                  <div className="rating-report-meta">
                    <span>
                      {t('reportsView.sections', { count: Object.keys(ratingReport.content.sections).length })}
                    </span>
                    <span className="meta-divider">•</span>
                    <span>
                      {t('reportsView.updated', { time: formatRelativeTime(ratingReport.updated_at.toDate()) })}
                    </span>
                  </div>
                </div>
                <div className="rating-report-actions">
                  {ratingReport.status === 'draft' ? (
                    <button
                      className="btn-primary"
                      onClick={() => navigate(`/reports/${ratingReport.id}`, {
                        state: { returnUrl: `/analyst-bench/${institutionId}?tab=reports&period=${selectedPeriod}` }
                      })}
                    >
                      {t('timeline.continueEditing')}
                    </button>
                  ) : (
                    <button
                      className="btn-outline"
                      onClick={() => navigate(`/reports/${ratingReport.id}`, {
                        state: { returnUrl: `/analyst-bench/${institutionId}?tab=reports&period=${selectedPeriod}` }
                      })}
                    >
                      {t('reportsView.viewReport')}
                    </button>
                  )}
                </div>
              </div>

              {/* Workflow Timeline - Key Indicators Style */}
              {timelineEvents.length > 0 && (
                <div className="workflow-steps">
                  <div className="workflow-steps-header">
                    <span className="workflow-steps-title">{t('reportsView.workflowProgress')}</span>
                  </div>
                  <div className="workflow-steps-list">
                    {timelineEvents.map((event) => {
                      const isExpanded = expandedTimelineItems.has(event.id);
                      const hasContent = event.status === 'completed' && (event.date || event.notes);

                      return (
                        <div key={event.id} className="workflow-step-wrapper">
                          <button
                            className={`workflow-step ${event.status}${event.id === 'rejected' ? ' rejected' : ''}${isExpanded ? ' expanded' : ''}`}
                            onClick={() => hasContent && toggleTimelineItem(event.id)}
                            disabled={!hasContent}
                          >
                            <span className={`step-badge ${event.status}${event.id === 'rejected' ? ' rejected' : ''}`}>
                              {event.status === 'completed' ? (
                                event.id === 'rejected' ? 'X' : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )
                              ) : event.status === 'current' ? (
                                <div className="step-badge-pulse" />
                              ) : (
                                getStepNumber(event.id)
                              )}
                            </span>
                            <span className="step-label">
                              {event.label}
                              {event.status === 'completed' && event.date && (
                                <span className="step-meta">
                                  {event.userName
                                    ? `${event.userName} • `
                                    : event.userId
                                      ? `${getTimelineUserName(event.userId)} • `
                                      : ''}
                                  {formatTimelineDate(event.date)}
                                </span>
                              )}
                              {event.status === 'current' && (
                                <span className="step-meta current">{t('reportsView.inProgress')}</span>
                              )}
                              {event.status === 'pending' && (
                                <span className="step-meta pending">{t('reportsView.pending')}</span>
                              )}
                            </span>
                            {event.notes && (
                              <span className="step-notes-badge" title="Has notes">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                              </span>
                            )}
                            {hasContent && (
                              <svg
                                className={`step-chevron ${isExpanded ? 'expanded' : ''}`}
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            )}
                          </button>

                          {/* Expanded content */}
                          {isExpanded && hasContent && (
                            <div className="workflow-step-details">
                              {event.notes && (
                                <div className="step-notes">
                                  <span className="step-notes-label">{t('reportsView.notes')}</span>
                                  <p className="step-notes-content">{event.notes}</p>
                                </div>
                              )}
                              {!event.notes && event.date && (
                                <div className="step-no-notes">
                                  {t('reportsView.noNotes')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rating-report-empty">
              <div className="rating-report-empty-content">
                <p>{t('reportsView.noRatingReport')}</p>
                <p className="rating-report-empty-hint">
                  {t('reportsView.createNarrative')}
                </p>
              </div>

              {/* AI Draft Generation Progress */}
              {draftGenerationProgress.isGenerating ? (
                <div className="draft-generation-progress">
                  <div className="draft-progress-header">
                    <div className="generating-spinner" />
                    <span className="draft-progress-title">{t('reportsView.generatingAiDraft')}</span>
                  </div>
                  <div className="draft-progress-bar-container">
                    <div
                      className="draft-progress-bar"
                      style={{ width: `${draftGenerationProgress.progress}%` }}
                    />
                  </div>
                  <p className="draft-progress-message">
                    {draftGenerationProgress.currentSection
                      ? t('reportsView.generating', { section: draftGenerationProgress.currentSection })
                      : draftGenerationProgress.message}
                  </p>
                  <p className="draft-progress-hint">
                    {t('reportsView.generatingHint')}
                  </p>
                </div>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleStartRatingReport}
                >
                  {t('reportsView.startRatingReport')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Data Report Section - hidden for now, will be used later */}
        {false && (
          <>
            {/* Divider */}
            <div className="section-divider">
              <span>Data Report</span>
            </div>

            {/* Cached Report Banner */}
            {checkingCache && (
              <div className="cached-report-banner loading">
                <span>Checking for existing reports...</span>
              </div>
            )}

            {!checkingCache && cachedReport && (
              <div className="cached-report-banner">
                <div className="cached-report-info">
                  <span className="cached-label">Report available for this period</span>
                  <span className="cached-date">
                    Generated {getRelativeTime(cachedReport.generated_at)}
                  </span>
                </div>
                <div className="cached-report-actions">
                  <button
                    className="btn-outline"
                    onClick={() => handleViewReport(cachedReport.report_id)}
                  >
                    View Report
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={handleGenerateReport}
                  >
                    {t('timeline.regenerate')}
                  </button>
                </div>
              </div>
            )}

            {/* Active Job Banner */}
            {activeJob && activeJob.status === 'generating' && (
              <div className="cached-report-banner generating">
                <div className="cached-report-info">
                  <span className="cached-label">Generating report...</span>
                  <span className="cached-date">Please wait</span>
                </div>
                <div className="generating-spinner" />
              </div>
            )}

            {activeJob && activeJob.status === 'completed' && activeJob.reportId && (
              <div className="cached-report-banner completed">
                <div className="cached-report-info">
                  <span className="cached-label">Report ready!</span>
                  <span className="cached-date">Generation completed</span>
                </div>
                <button
                  className="btn-primary"
                  onClick={() => handleViewReport(activeJob.reportId!)}
                >
                  View Report
                </button>
              </div>
            )}

            {/* Section Selection */}
            <div className="builder-sections">
              <div
                className="section-header"
                onClick={() => toggleSection('sections')}
              >
                <h4>Sections to Include</h4>
                <svg
                  className={`section-icon ${expandedSections.sections ? 'expanded' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>

              {expandedSections.sections && (
                <div className="section-content">
                  {/* Estructura de Balance */}
                  <div className="nested-section">
                    <label className="section-checkbox main-section">
                      <input
                        type="checkbox"
                        checked={options.sections.estructura_balance}
                        onChange={() => handleSectionToggle('estructura_balance')}
                      />
                      <span>Balance Sheet Structure</span>
                      <button
                        type="button"
                        className="expand-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleSection('estructura_balance');
                        }}
                      >
                        <svg
                          className={`expand-icon ${expandedSections.estructura_balance ? 'expanded' : ''}`}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </label>

                    {expandedSections.estructura_balance && (
                      <div className="subsections">
                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={estructuraBalance.activo.selected}
                              onChange={() => handleEstructuraSubsectionToggle('activo')}
                              disabled={!options.sections.estructura_balance}
                            />
                            <span>Assets (Activo)</span>
                          </label>
                          {estructuraBalance.activo.selected && (
                            <div className="chart-note">Chart 07: Income/Productive Assets</div>
                          )}
                        </div>

                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={estructuraBalance.pasivo.selected}
                              onChange={() => handleEstructuraSubsectionToggle('pasivo')}
                              disabled={!options.sections.estructura_balance}
                            />
                            <span>Liabilities (Pasivo)</span>
                          </label>
                          {estructuraBalance.pasivo.selected && (
                            <div className="chart-note">Chart 08: Cost/Productive Assets</div>
                          )}
                        </div>

                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={estructuraBalance.patrimonio.selected}
                              onChange={() => handleEstructuraSubsectionToggle('patrimonio')}
                              disabled={!options.sections.estructura_balance}
                            />
                            <span>Equity (Patrimonio)</span>
                          </label>
                          {estructuraBalance.patrimonio.selected && (
                            <div className="chart-note">Chart 09: Net Financial Margin</div>
                          )}
                        </div>

                        <div className="table-note">
                          Table 04: Balance Evolution (USD millions)
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Indicadores Financieros */}
                  <div className="nested-section">
                    <label className="section-checkbox main-section">
                      <input
                        type="checkbox"
                        checked={options.sections.indicadores_financieros}
                        onChange={() => handleSectionToggle('indicadores_financieros')}
                      />
                      <span>Financial Indicators</span>
                      <button
                        type="button"
                        className="expand-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleSection('indicadores_financieros');
                        }}
                      >
                        <svg
                          className={`expand-icon ${expandedSections.indicadores_financieros ? 'expanded' : ''}`}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </label>

                    {expandedSections.indicadores_financieros && (
                      <div className="subsections">
                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={indicadoresFinancieros.solvencia.selected}
                              onChange={() => handleIndicadoresSubsectionToggle('solvencia')}
                              disabled={!options.sections.indicadores_financieros}
                            />
                            <span>Solvency</span>
                          </label>
                          {indicadoresFinancieros.solvencia.selected && (
                            <div className="chart-note">Chart 10: Solvency Indicators</div>
                          )}
                        </div>

                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={indicadoresFinancieros.liquidez.selected}
                              onChange={() => handleIndicadoresSubsectionToggle('liquidez')}
                              disabled={!options.sections.indicadores_financieros}
                            />
                            <span>Liquidity</span>
                          </label>
                          {indicadoresFinancieros.liquidez.selected && (
                            <>
                              <div className="chart-note">Chart 11: Liquidity Indicators</div>
                              <div className="table-note">Table 05: Investment Portfolio</div>
                            </>
                          )}
                        </div>

                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={indicadoresFinancieros.calidad_activos.selected}
                              onChange={() => handleIndicadoresSubsectionToggle('calidad_activos')}
                              disabled={!options.sections.indicadores_financieros}
                            />
                            <span>Asset Quality</span>
                          </label>
                          {indicadoresFinancieros.calidad_activos.selected && (
                            <div className="chart-note">Chart 12: Asset Quality Indicators</div>
                          )}
                        </div>

                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={indicadoresFinancieros.eficiencia_gestion.selected}
                              onChange={() => handleIndicadoresSubsectionToggle('eficiencia_gestion')}
                              disabled={!options.sections.indicadores_financieros}
                            />
                            <span>Efficiency & Management</span>
                          </label>
                          {indicadoresFinancieros.eficiencia_gestion.selected && (
                            <div className="chart-note">Chart 13: Efficiency Indicators</div>
                          )}
                        </div>

                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={indicadoresFinancieros.rentabilidad.selected}
                              onChange={() => handleIndicadoresSubsectionToggle('rentabilidad')}
                              disabled={!options.sections.indicadores_financieros}
                            />
                            <span>Profitability</span>
                          </label>
                        </div>

                        <div className="subsection">
                          <label className="subsection-checkbox">
                            <input
                              type="checkbox"
                              checked={indicadoresFinancieros.riesgo_interes.selected}
                              onChange={() => handleIndicadoresSubsectionToggle('riesgo_interes')}
                              disabled={!options.sections.indicadores_financieros}
                            />
                            <span>Interest Rate Risk</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Anexo 1 */}
                  <label className="section-checkbox standalone-section">
                    <input
                      type="checkbox"
                      checked={includeAnexo1}
                      onChange={() => setIncludeAnexo1(!includeAnexo1)}
                    />
                    <span>Annex 1: Complete Historical Table</span>
                  </label>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            {progress.status !== 'idle' && (
              <div className="builder-progress">
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: `${progress.progress}%` }} />
                </div>
                <p className="progress-message">{progress.message}</p>
                {progress.error && (
                  <p className="progress-error">Error: {progress.error}</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
